import { Divider } from '@zillow/constellation-10';
import type { CreditReportTypeWithStatement } from '@zg-rentals/applications-utils';
import { type TrackEvent, UserType } from '../models/report-models';
import { CreditReportHeader } from './components/CreditReportHeader';
import { CreditReportStatements } from './components/CreditReportStatements';
import { CreditReportScore } from './components/CreditReportScore';
import { CreditReportSummary } from './components/CreditReportSummary';
import { CreditReportAccounts } from './components/CreditReportAccounts';
import { CreditReportCollections } from './components/CreditReportCollections';
import { CreditReportInquiries } from './components/CreditReportInquiries';
import { CreditReportPublicRecords } from './components/CreditReportPublicRecords';
import { CreditReportFooter } from './components/CreditReportFooter';
import type { EmitMetric } from '../models/report-models';
import { formatDate } from '@zg-rentals/formatters';

interface CreditReportProps {
  userType?: UserType;
  creditReport: CreditReportTypeWithStatement;
  requestedDateTime: Date | string | null | undefined;
  expirationDateTime: Date | string | null | undefined;
  onDownloadPdf?: () => void;
  isDownloadingPdf?: boolean;
  isInMobileWebView?: boolean;
  trackEvent: TrackEvent;
  emitMetric: EmitMetric;
}

export default function CreditReport({
  userType = UserType.Renter,
  creditReport,
  requestedDateTime,
  expirationDateTime,
  onDownloadPdf,
  isDownloadingPdf,
  isInMobileWebView = false,
  trackEvent,
  emitMetric,
}: CreditReportProps) {
  const formattedRequestedDateTime = formatDate(requestedDateTime);
  const formattedExpirationDateTime = formatDate(expirationDateTime);

  return (
    <div id="credit-report">
      <CreditReportHeader
        creditReport={creditReport}
        requestedDateTime={formattedRequestedDateTime}
        expirationDateTime={formattedExpirationDateTime}
        onDownloadPdf={onDownloadPdf}
        isDownloadingPdf={isDownloadingPdf}
        isInMobileWebView={isInMobileWebView}
        trackEvent={trackEvent}
      />
      <Divider css={{ marginTop: 'sm', marginBottom: 'sm' }} />
      <CreditReportStatements creditReport={creditReport} />
      <CreditReportScore creditReport={creditReport} />
      <CreditReportSummary creditReport={creditReport} userType={userType} />
      <CreditReportAccounts creditReport={creditReport} emitMetric={emitMetric} />
      <CreditReportCollections creditReport={creditReport} emitMetric={emitMetric} />
      <CreditReportInquiries creditReport={creditReport} emitMetric={emitMetric} />
      <CreditReportPublicRecords creditReport={creditReport} emitMetric={emitMetric} />
      <Divider css={{ marginTop: 'sm', marginBottom: 'sm' }} />
      <CreditReportFooter userType={userType} emitMetric={emitMetric} />
    </div>
  );
}
